<template>
  <DefaultCrudView
    :breadcrumbsItems="breadcrumbs"
    :actions="availableCrudActions"
    @create-new="newServer"
    containerLarge
  >
    <template slot="content">
      <v-data-table
        :headers="headers"
        :items="servers"
        :loading="loading"
        :options.sync="options"
        :server-items-length="itemsLength"
        :footer-props="$defaultDataTableFooterProps"
        class="elevation-1"
      >
        <template v-slot:item.tenant_id="{ item }">
          {{ getTenantTitle(item.tenant_id) }}
        </template>

        <template v-slot:item.actions="{ item }">
          <v-tooltip v-if="isMaster" left>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" class="mr-2" @click="editServer(item)">mdi-pencil</v-icon>
            </template>
            <span>{{ $t('edit') }}</span>
          </v-tooltip>
          <v-tooltip left>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" class="mr-2" @click="openRoutineDialog(item)">mdi-playlist-check</v-icon>
            </template>
            <span>{{ $t('manage_server_routines') }}</span>
          </v-tooltip>
          <v-tooltip v-if="isMaster" left>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" @click="confirmDelete(item)">mdi-delete</v-icon>
            </template>
            <span>{{ $t('delete') }}</span>
          </v-tooltip>
        </template>
      </v-data-table>

      <DefaultModal :title="modalTitle" ref="serverModal" @submit="saveServer">
        <template v-slot:content>
          <v-form ref="form" v-model="valid">
            <v-select
              v-model="selectedServer.tenant_id"
              :items="tenantOptions"
              item-text="title"
              item-value="tenant_id"
              :label="$t('tenant')"
              :rules="[v => v !== null && v !== undefined || $t('tenant_required')]"
              outlined
              dense
            />
            <v-text-field
              v-model="selectedServer.server_key"
              :label="$t('server_key')"
              :rules="[v => !!v || $t('field_required')]"
              outlined
              dense
            />
            <v-text-field
              v-model="selectedServer.server_url"
              :label="$t('server_url')"
              :rules="[v => !!v || $t('field_required')]"
              outlined
              dense
            />
          </v-form>
        </template>
      </DefaultModal>

      <DefaultModal :title="$t('server_routines')" ref="routineModal" @submit="saveRoutineLinks">
        <template v-slot:content>
          <v-select
            v-model="selectedRoutineIds"
            :items="allRoutines"
            item-text="routine_key"
            item-value="routine_id"
            :label="$t('routines')"
            multiple
            chips
            outlined
          />
        </template>
      </DefaultModal>

      <DefaultModal :title="$t('delete')" ref="deleteModal" @submit="deleteServer" deleteModal>
        <template v-slot:content>
          {{ $t('remove_entry_confirmation') }} <strong>{{ serverToDelete && serverToDelete.server_key }}</strong>?
        </template>
      </DefaultModal>
    </template>
  </DefaultCrudView>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import DefaultCrudView from '@/components/DefaultCrudView.vue'
import DefaultModal from '@/components/DefaultModal.vue'
import serverApi from '@/services/api/pipeline/server'
import routineApi from '@/services/api/pipeline/routine'
import routineServerApi from '@/services/api/pipeline/routineserver'
import TenantActions from '@/store/core/tenant/actions-types'
import AppActions from '@/store/app/actions-types'
import UserRoles from '@/store/core/user/user-roles'

export default {
  name: 'Server',

  components: { DefaultCrudView, DefaultModal },

  data() {
    return {
      loading: true,
      valid: false,
      servers: [],
      tenants: [],
      options: { sortBy: ['server_key'], sortDesc: [false] },
      itemsLength: 0,
      selectedServer: this.emptyServer(),
      serverToDelete: null,
      selectedRoutineServer: null,
      allRoutines: [],
      attachedRoutineIds: [],
      selectedRoutineIds: [],
      crudActions: [{ text: 'create_server', eventToEmit: 'create-new' }],
      headers: [
        { text: this.$t('server_id'), value: 'server_id' },
        { text: this.$t('server_key'), value: 'server_key' },
        { text: this.$t('server_url'), value: 'server_url' },
        { text: this.$t('tenant'), value: 'tenant_id' },
        { text: this.$t('actions'), value: 'actions', sortable: false },
      ],
    }
  },

  computed: {
    ...mapGetters('app', ['getIconForOperationSettingsItem']),
    ...mapGetters('core/auth', ['checkAccessRights']),

    isMaster() {
      return this.checkAccessRights([UserRoles.USER_MASTER])
    },

    availableCrudActions() {
      return this.isMaster ? this.crudActions : []
    },

    breadcrumbs() {
      return [{ text: this.$t('servers'), prependIcon: this.getIconForOperationSettingsItem('servers') }]
    },

    tenantOptions() {
      const userData = this.$store.state.core.auth.userData || {}
      const adminTenantIds = (userData.admins || []).map(admin => admin.tenant_id)
      return adminTenantIds.length ? this.tenants.filter(tenant => adminTenantIds.includes(tenant.tenant_id)) : this.tenants
    },

    modalTitle() {
      return this.selectedServer.server_id ? this.$t('edit_server') : this.$t('new_server')
    },
  },

  watch: {
    options: {
      handler() { this.reloadData() },
      deep: true,
    },
  },

  mounted() {
    this.loadTenants()
  },

  methods: {
    ...mapActions('core/tenant', [TenantActions.GET_ALL_TENANTS]),
    ...mapActions('app', [AppActions.OPEN_APP_SUCCESS_MESSAGE, AppActions.OPEN_APP_ERROR_MESSAGE]),

    emptyServer() {
      return { server_key: '', server_url: '', tenant_id: null }
    },

    async loadTenants() {
      try {
        const result = await this[TenantActions.GET_ALL_TENANTS]()
        this.tenants = result.items || result.data || result || []
      } catch (error) {
        this[AppActions.OPEN_APP_ERROR_MESSAGE](this.$t('error_loading_tenants'))
      }
    },

    reloadData() {
      const params = { ...this.$route.query }
      if (this.options.sortBy.length) params.order = this.options.sortBy[0]
      if (this.options.sortDesc[0]) params.direction = 'DESC'
      params.limit = this.options.itemsPerPage
      params.offset = this.options.itemsPerPage * (this.options.page - 1)

      this.loading = true
      serverApi.getAllServers(params)
        .then(response => {
          const result = response.data
          this.servers = result.items || result.data || result || []
          this.itemsLength = result.total || this.servers.length
        })
        .catch(() => this[AppActions.OPEN_APP_ERROR_MESSAGE]('Error loading servers'))
        .finally(() => { this.loading = false })
    },

    newServer() {
      if (!this.isMaster) return

      this.selectedServer = this.emptyServer()
      if (this.tenantOptions.length === 1) this.selectedServer.tenant_id = this.tenantOptions[0].tenant_id
      this.$refs.serverModal.openModal()
    },

    editServer(server) {
      if (!this.isMaster) return

      this.selectedServer = { ...server }
      this.$refs.serverModal.openModal()
    },

    saveServer() {
      if (!this.isMaster) return
      if (!this.$refs.form.validate()) return

      const data = {
        server_key: this.selectedServer.server_key,
        server_url: this.selectedServer.server_url,
        tenant_id: this.selectedServer.tenant_id,
      }
      const request = this.selectedServer.server_id
        ? serverApi.updateServer(this.selectedServer.server_id, data)
        : serverApi.createServer(data)

      request
        .then(() => {
          this.$refs.serverModal.closeModal()
          this[AppActions.OPEN_APP_SUCCESS_MESSAGE](this.$t('save_successful'))
          this.reloadData()
        })
        .catch(error => this[AppActions.OPEN_APP_ERROR_MESSAGE](error.response?.data?.message || 'Error saving server'))
        .finally(() => { this.$refs.serverModal.submitting = false })
    },

    confirmDelete(server) {
      if (!this.isMaster) return

      this.serverToDelete = server
      this.$refs.deleteModal.openModal()
    },

    deleteServer() {
      if (!this.isMaster || !this.serverToDelete) return

      serverApi.deleteServer(this.serverToDelete.server_id)
        .then(() => {
          this.$refs.deleteModal.closeModal()
          this[AppActions.OPEN_APP_SUCCESS_MESSAGE](this.$t('remove_successful'))
          this.reloadData()
        })
        .catch(error => this[AppActions.OPEN_APP_ERROR_MESSAGE](error.response?.data?.message || 'Error deleting server'))
        .finally(() => { this.$refs.deleteModal.submitting = false })
    },

    async openRoutineDialog(server) {
      this.selectedRoutineServer = server
      this.loading = true
      try {
        const [allResponse, attachedResponse] = await Promise.all([
          routineApi.getAllRoutines({ order: 'routine_key', direction: 'ASC', limit: 200, offset: 0 }),
          routineApi.getRoutinesByServer(server.server_id),
        ])
        const all = allResponse.data
        const attached = attachedResponse.data
        this.allRoutines = all.items || all.data || all || []
        this.attachedRoutineIds = (attached.items || attached.data || attached || []).map(routine => routine.routine_id)
        this.selectedRoutineIds = [...this.attachedRoutineIds]
        this.$refs.routineModal.openModal()
      } catch (error) {
        this[AppActions.OPEN_APP_ERROR_MESSAGE]('Error loading server routines')
      } finally {
        this.loading = false
      }
    },

    async saveRoutineLinks() {
      const serverId = this.selectedRoutineServer.server_id
      const selected = this.selectedRoutineIds.map(Number)
      const attached = this.attachedRoutineIds.map(Number)
      const toCreate = selected.filter(routineId => !attached.includes(routineId))
      const toDelete = attached.filter(routineId => !selected.includes(routineId))

      try {
        await Promise.all([
          ...toCreate.map(routineId => routineServerApi.createRoutineServer(serverId, routineId)),
          ...toDelete.map(routineId => routineServerApi.deleteRoutineServer(serverId, routineId)),
        ])
        this.$refs.routineModal.closeModal()
        this[AppActions.OPEN_APP_SUCCESS_MESSAGE](this.$t('save_successful'))
      } catch (error) {
        this[AppActions.OPEN_APP_ERROR_MESSAGE](error.response?.data?.message || 'Error saving server routines')
      } finally {
        this.$refs.routineModal.submitting = false
      }
    },

    getTenantTitle(tenantId) {
      const tenant = this.tenants.find(item => item.tenant_id == tenantId)
      return tenant ? tenant.title : tenantId
    },
  },
}
</script>
